<template>
    <ModalShell title="Add Child" subtitle="Enter your child's information to add them to your protection plan.">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#181D27" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="4.5" r="2.5" /><path d="m10.2 15.5-1.7 4.7a.7.7 0 0 0 .7.8h5.6a.7.7 0 0 0 .7-.8l-1.7-4.7" /><path d="M15.5 10a3.5 3.5 0 1 0-7 0v5.5h7z" />
            </svg>
        </template>
        <!-- Form fields -->
        <div class="form-section">
            <!-- First Name and Last Name row -->
            <div class="form-row">
                <div class="form-field">
                    <label class="field-label">
                        First Name
                        <span class="required">*</span>
                    </label>
                    <PrimeInputText
                        v-model="firstName"
                        placeholder="First name"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                </div>
                <div class="form-field">
                    <label class="field-label">
                        Last Name
                        <span class="required">*</span>
                    </label>
                    <PrimeInputText
                        v-model="lastName"
                        placeholder="Last name"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                </div>
            </div>

            <!-- Date of Birth -->
            <div class="form-field">
                <label class="field-label">
                    Date of Birth
                    <span class="required">*</span>
                </label>
                <PrimeDatePicker
                    v-model="dob"
                    placeholder="Select date of birth"
                    dateFormat="mm/dd/yy"
                    :maxDate="maxDate"
                />
                <div v-if="dobError" class="validation-message">
                    <span>{{ dobError }}</span>
                </div>
            </div>
        </div>
        <template #footer>
            <PrimeButton
                label="Cancel"
                class="p-btn-root_secondary"
                @click="closeDialog"
            />
            <PrimeButton
                label="Add Child"
                variant="primary"
                :disabled="!canSubmit"
                :loading="isSubmitting"
                @click="onSubmit"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import PrimeInputText from 'primevue/inputtext';
import PrimeDatePicker from 'primevue/datepicker';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { useContactsStore } from '~/stores/person/contacts';
import { ModalNames } from '~/types/modals';

const { letterOnlyInputHandler } = useUtils();
const { calculateAge } = useDateHelpers();
const contactsStore = useContactsStore();

const dialogRef = getDialogRef(ModalNames.ADD_CHILD_FORM);

// Form state
const firstName = ref('');
const lastName = ref('');
const dob = ref<Date | null>(null);
const isSubmitting = ref(false);

// Max date = today (child must already be born)
const maxDate = new Date();

const dobError = computed(() => {
    if (!dob.value) return '';

    const age = calculateAge(dob.value);

    if (age !== null && age >= 18) {
        return 'Child must be under 18 years old. Use "Protect Another Adult" for adults.';
    }

    return '';
});

const canSubmit = computed(() => {
    return firstName.value.trim().length > 0 &&
           lastName.value.trim().length > 0 &&
           dob.value !== null &&
           !dobError.value &&
           !isSubmitting.value;
});

const closeDialog = () => {
    dialogRef.value.close();
};

const onEnter = (e: KeyboardEvent) => {
    e.preventDefault();
    if (canSubmit.value) {
        onSubmit();
    }
};

async function onSubmit() {
    if (!canSubmit.value) return;

    isSubmitting.value = true;

    // Format DOB as YYYY-MM-DD
    const birthDate = new Date(dob.value!);
    const formattedDob = `${birthDate.getFullYear()}-${String(birthDate.getMonth() + 1).padStart(2, '0')}-${String(birthDate.getDate()).padStart(2, '0')}`;

    const success = await contactsStore.createMinorChild({
        first_name: firstName.value.trim(),
        last_name: lastName.value.trim(),
        dob: formattedDob,
    });

    if (success) {
        closeDialog();
    }

    isSubmitting.value = false;
}
</script>

<style lang="scss" scoped>
.form-section {
    @apply flex flex-col gap-4 w-full;
}

.form-row {
    @apply flex sm:flex-row flex-col gap-4;
}

.form-field {
    @apply flex flex-col gap-1.5 flex-1;
}

.field-label {
    @apply text-sm font-medium text-zinc-700 leading-5;

    .required {
        @apply text-blue-600 ml-0.5;
    }
}

.validation-message {
    @apply flex gap-1 mt-1;

    span {
        @apply text-red-600 font-inter text-sm font-normal leading-5;
    }
}
</style>
